import type { ArtworkVariantKind } from "../schemas"

export const ACCEPTED_ARTWORK_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
] as const

export const ACCEPTED_ARTWORK_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".heic",
  ".heif",
  ".gif",
] as const

export const MAX_ARTWORK_UPLOAD_BYTES = 12 * 1024 * 1024
export const MAX_ARTWORK_DECODED_PIXELS = 16_000_000
export const MAX_ARTWORK_DIMENSION = 4096
export const MAX_ARTWORK_PROCESSING_WALL_TIME_MS = 10_000
export const MAX_ARTWORK_PROCESSING_CPU_TIME_MS = 5_000

export const ARTWORK_MEDIA_PATH_PREFIX = "/media/artwork"

export const ARTWORK_VARIANT_BOUNDS = {
  card_art: {
    width: 640,
    height: 896,
    targetBytes: 350 * 1024,
  },
  thumb: {
    width: 160,
    height: 224,
    targetBytes: 60 * 1024,
  },
} as const

export type ArtworkUploadValidationIssue = {
  code:
    | "ARTWORK_UNSUPPORTED_MIME_TYPE"
    | "ARTWORK_UNSUPPORTED_EXTENSION"
    | "ARTWORK_FILE_TOO_LARGE"
    | "ARTWORK_INVALID_DIMENSIONS"
  message: string
}

export function isAcceptedArtworkMimeType(mimeType: string): boolean {
  return (ACCEPTED_ARTWORK_MIME_TYPES as readonly string[]).includes(mimeType)
}

export function isAcceptedArtworkExtension(extension: string): boolean {
  return (ACCEPTED_ARTWORK_EXTENSIONS as readonly string[]).includes(
    extension.toLowerCase(),
  )
}

export function isWithinArtworkDimensionLimits(
  width: number,
  height: number,
): boolean {
  if (width < 1 || height < 1) return false
  if (width > MAX_ARTWORK_DIMENSION || height > MAX_ARTWORK_DIMENSION) {
    return false
  }
  return width * height <= MAX_ARTWORK_DECODED_PIXELS
}

export function buildArtworkVariantPath(
  assetId: string,
  kind: ArtworkVariantKind,
): string {
  return `${ARTWORK_MEDIA_PATH_PREFIX}/${assetId}/${kind}.webp`
}

export function buildArtworkVariantUrl(
  baseUrl: string,
  assetId: string,
  kind: ArtworkVariantKind,
): string {
  return new URL(buildArtworkVariantPath(assetId, kind), baseUrl).toString()
}

export function validateArtworkUploadInput(input: {
  mimeType: string
  extension?: string
  byteLength: number
  width?: number
  height?: number
}): ArtworkUploadValidationIssue[] {
  const issues: ArtworkUploadValidationIssue[] = []

  if (!isAcceptedArtworkMimeType(input.mimeType)) {
    issues.push({
      code: "ARTWORK_UNSUPPORTED_MIME_TYPE",
      message: `Unsupported artwork MIME type "${input.mimeType}".`,
    })
  }

  if (
    input.extension != null &&
    input.extension.length > 0 &&
    !isAcceptedArtworkExtension(input.extension)
  ) {
    issues.push({
      code: "ARTWORK_UNSUPPORTED_EXTENSION",
      message: `Unsupported artwork file extension "${input.extension}".`,
    })
  }

  if (input.byteLength < 1 || input.byteLength > MAX_ARTWORK_UPLOAD_BYTES) {
    issues.push({
      code: "ARTWORK_FILE_TOO_LARGE",
      message: `Artwork upload exceeds the ${MAX_ARTWORK_UPLOAD_BYTES} byte limit.`,
    })
  }

  if (
    input.width != null &&
    input.height != null &&
    !isWithinArtworkDimensionLimits(input.width, input.height)
  ) {
    issues.push({
      code: "ARTWORK_INVALID_DIMENSIONS",
      message: "Artwork dimensions exceed the allowed decode budget.",
    })
  }

  return issues
}
